import { useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { API_CONFIG } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import { useSocketReconnect } from './useSocketReconnect'

type SocketEventMap = Record<string, (payload: never) => void>

interface UseAuthedSocketOptions<E extends SocketEventMap> {
  events: E
  reconnectQueryKeys?: Array<string | ReadonlyArray<string | number>>
  /** `false` mantém o socket desligado (ex.: portal externo, só em dev). Padrão `true`. */
  enabled?: boolean
  /** Ordem de transportes. Padrão `['websocket', 'polling']`. */
  transports?: Array<'websocket' | 'polling'>
}

const RETRY_BASE_MS = 1000
const RETRY_MAX_MS = 30000

export function useAuthedSocket<E extends SocketEventMap>(
  namespace: string,
  opts: UseAuthedSocketOptions<E> = { events: {} as E },
): { socket: Socket | null } {
  const { token, user } = useAuth()
  const socketRef = useRef<Socket | null>(null)
  const [socket, setSocket] = useState<Socket | null>(null)
  // Reconecta só se o escopo mudar: setUser(profile) gera objeto novo a cada
  // refresh e derrubava/reabria o socket em toda carga.
  const enabled = opts.enabled ?? true
  const transportsKey = (opts.transports ?? ['websocket', 'polling']).join(',')
  const userKey = user ? `${user.id}|${user.user_type_code ?? ''}|${user.cost_center_id ?? ''}` : null

  useEffect(() => {
    if (!enabled || !token || !userKey) {
      // Logout: garante que nenhuma conexão fique aberta.
      socketRef.current?.close()
      socketRef.current = null
      setSocket(null)
      return
    }
    const s = io(`${API_CONFIG.BASE_URL}${namespace}`, {
      withCredentials: true,
      transports: transportsKey.split(',') as Array<'websocket' | 'polling'>,
      auth: { token },
    })
    socketRef.current = s
    setSocket(s)
    for (const [event, handler] of Object.entries(opts.events)) {
      s.on(event, handler as (payload: unknown) => void)
    }
    // socket.io-client destrói o socket em CONNECT_ERROR (sem auto-reconexão).
    // Falha temporária do handshake ('unavailable', timeout) → religa com
    // backoff exponencial limitado; 'unauthorized' é definitivo e não repete.
    let attempt = 0
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    const clearRetry = () => {
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = null
    }
    const onConnect = () => {
      attempt = 0
      clearRetry()
      s.off('connect', onConnect)
    }
    const onConnectError = (err: Error) => {
      if (err?.message === 'unauthorized') return
      clearRetry()
      // Registrado só depois de uma falha: a montagem não ganha listener extra.
      s.off('connect', onConnect)
      s.on('connect', onConnect)
      const delay = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS)
      attempt += 1
      retryTimer = setTimeout(() => {
        retryTimer = null
        s.connect()
      }, delay)
    }
    s.on('connect_error', onConnectError)
    return () => {
      clearRetry()
      s.off('connect', onConnect)
      s.off('connect_error', onConnectError)
      for (const [event, handler] of Object.entries(opts.events)) {
        s.off(event, handler as (payload: unknown) => void)
      }
      s.close()
      socketRef.current = null
      setSocket(null)
    }
     
  }, [token, userKey, namespace, enabled, transportsKey])

  useSocketReconnect(socket, opts.reconnectQueryKeys ?? [])

  return { socket }
}
